import './popupListaLavori.scss';

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, DataGrid, LoadPanel, Popup, Toast, ActionSheet } from "devextreme-react";
import { Position, ToolbarItem } from "devextreme-react/popup";
import { DataGridRef, Column, Sorting, DataGridTypes } from "devextreme-react/data-grid";
import { confirm } from "devextreme/ui/dialog";
import { LavoriPopupEdit, LavoriPopupSpese, LavoriPopupCopia } from "..";
import { PopupEditHandle } from "./popupEdit";
import { PopupSpeseHandle } from "./popupSpese";
import { PopupCopiaLavoroHandle } from "../../components/popup-lavori/popupCopiaLavoro";
import { LavoriApi } from "../../data";
import useApiConfiguration from "../../apiConfig";
import { useScreenSize } from "../../utils/media-query";

export type PopupListaHandle = {
    show: (idPersonale: number, data: Date) => void
}

type Props = {
    onHide: () => void;
}

type State = {
    idPersonale: number,
    data: Date,
    visible: boolean
};

const PopupLista: React.ForwardRefRenderFunction<PopupListaHandle, Props> = (props, ref) => {
    const popupRef = useRef(null);
    const gridRef = useRef<DataGridRef>(null);
    const popupEditRef = useRef<PopupEditHandle>(null);
    const popupSpeseRef = useRef<PopupSpeseHandle>(null);
    const popupCopiaRef = useRef<PopupCopiaLavoroHandle>(null);

    const apiConfig = useApiConfiguration();
    const lavoriApi = new LavoriApi(apiConfig);
    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });
    const { isXSmall, isSmall } = useScreenSize();

    const [loading, setLoading] = useState<boolean>(false);
    const [enableEdit, setEnableEdit] = useState<boolean>(false);
    const [state, setState] = useState<State>({ idPersonale: 0, data: undefined, visible: false });
    const [lavoriDataSource, setLavoriDataSource] = useState<any>(null);
    const [actionVisible, setActionVisible] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);

    const hasAnyEditableRow = Array.isArray(lavoriDataSource) && lavoriDataSource.some(r => r.allowEdit === true);

    function hideInfo() {
        props.onHide();
        setLavoriDataSource([]);
        setState({ idPersonale: 0, data: undefined, visible: false });
    };

    useImperativeHandle(ref, () => ({
        show(idPersonale, data) {
            setState({ idPersonale: idPersonale, data: data, visible: true });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            setLoading(true);

            lavoriApi.getBloccoCaricamentoLavori({
                data: state.data,
                idPersonale: state.idPersonale
            }).then((res) => {
                setEnableEdit(res.allowEdit);
            }).finally(() => {
                refreshGrid();
            });
        }
    }, [state.visible, state.data, state.idPersonale]);

    function refreshGrid() {
        setLoading(true);

        lavoriApi.getLavori({
            personaleId: state.idPersonale,
            daData: state.data,
            aData: state.data
        }).then((res) => setLavoriDataSource(res))
            .finally(() => setLoading(false));
    };

    function openCopiaLavoro(idLavoro: number) {
        popupCopiaRef.current?.show(idLavoro);
    };

    function lavoroCopiato() {
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            message: 'Lavoro copiato correttamente'
        });
    };

    function openSpese(idLavoro: number, commessa: string) {
        popupSpeseRef.current?.show(idLavoro, commessa);
    };

    function updateLavoro(idLavoro: number) {
        popupEditRef.current?.edit(idLavoro, state.idPersonale, state.data);
    };

    function checkDelete(id: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deleteLavoro(id);
        })
    };

    function deleteLavoro(idLavoro: number) {
        lavoriApi.deleteLavoro({ id: idLavoro })
            .then(() => {
                refreshGrid();
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    message: 'Cancellazione completata'
                });
            })
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    function onRowClick(e) {
        if (e.event?.pointerType === "touch") {
            setSelectedRow(e.data);
            setActionVisible(enableEdit && e.data.allowEdit);
        }
    }

    return (
        <React.Fragment>
            <Popup
                visible={state.visible} fullScreen={(isSmall || isXSmall)}
                dragEnabled={false} hideOnOutsideClick={false}
                showCloseButton showTitle
                ref={popupRef} title="Lavori"
                container=".dx-viewport"
                height="85%"
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget="dxButton" toolbar="bottom" location="center">
                    <Button
                        onClick={hideInfo}
                        icon="close"
                        text="Chiudi"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                {
                    loading && <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor="rgba(0, 0, 0, 0.4)" position={'center'} visible={true} />
                }

                <div style={{ height: "100%" }} id="divGridLavori">
                    <DataGrid
                        id="gridLavori" ref={gridRef} key="idLavoro"
                        dataSource={lavoriDataSource}
                        showBorders rowAlternationEnabled noDataText="Nessun lavoro caricato"
                        className="grid theme-dependent" onRowClick={onRowClick}
                    >
                        <Sorting mode="single" />

                        <Column
                            visible={enableEdit && hasAnyEditableRow && !(isSmall || isXSmall)}
                            width={150}
                            cellRender={(cell) => (
                                <div style={{ display: "flex" }}>
                                    <Button icon="money" hint="Spese" stylingMode="text" visible={cell.data.allowEdit} onClick={() => openSpese(cell.data.idLavoro, cell.data.commessa)} />
                                    <Button icon="copy" hint="Copia" stylingMode="text" visible={cell.data.allowEdit} onClick={() => openCopiaLavoro(cell.data.idLavoro)} />
                                    <Button icon="edit" hint="Modifica" stylingMode="text" visible={cell.data.allowEdit} onClick={() => updateLavoro(cell.data.idLavoro)} />
                                    <Button icon="trash" hint="Elimina" stylingMode="text" visible={cell.data.allowEdit} onClick={() => checkDelete(cell.data.idLavoro)} />
                                </div>
                            )}
                        />
                        <Column dataField="idLavoro" caption="" visible={false} />
                        <Column dataField="commessa" caption="Commessa" />
                        <Column dataField="note" caption="Note" hidingPriority={0} />
                        <Column
                            dataField="oreLavorate"
                            alignment="center"
                            width={90}
                            caption="Ore"
                        />
                    </DataGrid>
                </div>
            </Popup>

            <ActionSheet
                visible={actionVisible} title='Azioni'
                onCancelClick={() => setActionVisible(false)}
                onItemClick={() => setActionVisible(false)}
                usePopover={false}
                dataSource={[
                    {
                        text: "Modifica",
                        icon: "edit",
                        onClick: () => updateLavoro(selectedRow.idLavoro)
                    },
                    {
                        text: "Spese",
                        icon: "money",
                        onClick: () => openSpese(selectedRow.idLavoro, selectedRow.commessa)
                    },
                    {
                        text: "Copia",
                        icon: "copy",
                        onClick: () => openCopiaLavoro(selectedRow.idLavoro)
                    },
                    {
                        text: "Elimina",
                        icon: "trash",
                        onClick: () => checkDelete(selectedRow.idLavoro)
                    }
                ]}
            />

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type="success"
                onHidden={onHidingToast} closeOnClick
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />

            <LavoriPopupEdit ref={popupEditRef} onChange={refreshGrid} />
            <LavoriPopupSpese ref={popupSpeseRef} onChange={refreshGrid} />
            <LavoriPopupCopia ref={popupCopiaRef} onChange={lavoroCopiato} />
        </React.Fragment>
    );
}

export default React.forwardRef(PopupLista);